import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type {
  CreateMaestroInput,
  UpdateMaestroInput,
  CreateMaestroRateInput,
  UpdateMaestroRateInput,
} from '@shared/validation/maestro-schemas';

// ── DTOs ──

export interface MaestroListDTO {
  id: string;
  nombre: string;
  cedula: string | null;
  telefono: string | null;
  especialidades: string[];
  is_active: boolean;
  is_planta: boolean;
  proyectos_activos: number;
  created_at: string;
  updated_at: string;
  sync_result?: { projects_updated: number; rates_added: number };
}

export interface RateMdoItemDTO {
  material_id: string;
  material_nombre: string;
  precio_unitario: number;
}

export interface MaestroRateDTO {
  id: string;
  recipe_id: string;
  recipe_codigo: string;
  recipe_nombre: string;
  precio_unitario: number;
  unidad: string;
  mdo_items: RateMdoItemDTO[];
}

export interface ActiveProjectDTO {
  project_id: string;
  project_codigo: string;
  project_nombre: string;
}

export interface MaestroDetailDTO extends MaestroListDTO {
  base_rates: MaestroRateDTO[];
  active_projects: ActiveProjectDTO[];
}

interface MaestrosResponse {
  data: MaestroListDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MaestroFilters {
  search?: string;
  especialidad?: string;
  isActive?: string;
  page?: number;
  limit?: number;
}

// ── List ──
export function useMaestros(filters?: MaestroFilters) {
  return useQuery({
    queryKey: queryKeys.maestros.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<MaestrosResponse>('/maestros', { params: filters });
      return response.data;
    },
  });
}

// ── Detail ──
export function useMaestro(id: string) {
  return useQuery({
    queryKey: queryKeys.maestros.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: MaestroDetailDTO }>(`/maestros/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

// ── Create ──
export function useCreateMaestro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMaestroInput) => {
      const response = await api.post<{ data: MaestroListDTO }>('/maestros', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.all });
      toast('success', `Maestro "${data.nombre}" creado`);
    },
  });
}

// ── Update ──
export function useUpdateMaestro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMaestroInput }) => {
      const response = await api.put<{ data: MaestroListDTO }>(`/maestros/${id}`, data);
      return response.data.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.detail(variables.id) });
      const sync = data.sync_result;
      if (sync && sync.rates_added > 0) {
        toast('success', `Maestro actualizado. Se asignaron ${sync.rates_added} APU(s) nuevos en ${sync.projects_updated} proyecto(s).`);
      } else {
        toast('success', 'Maestro actualizado');
      }
    },
  });
}

// ── Add base rate ──
export function useAddBaseRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ maestroId, data }: { maestroId: string; data: CreateMaestroRateInput }) => {
      const response = await api.post<{ data: { id: string; recipe_nombre: string; precio_unitario: number } }>(
        `/maestros/${maestroId}/rates`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.detail(variables.maestroId) });
      toast('success', `Tarifa guardada: $${_data.precio_unitario}`);
    },
  });
}

// ── Update base rate ──
export function useUpdateBaseRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      maestroId,
      rateId,
      data,
    }: {
      maestroId: string;
      rateId: string;
      data: UpdateMaestroRateInput;
    }) => {
      const response = await api.put<{ data: { id: string; precio_unitario: number } }>(
        `/maestros/${maestroId}/rates/${rateId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.detail(variables.maestroId) });
      toast('success', 'Tarifa actualizada');
    },
  });
}

// ── Delete maestro (soft) ──
export function useDeleteMaestro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/maestros/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maestros.all });
      toast('success', 'Maestro desactivado');
    },
  });
}
